import i18n from '@/i18n/config';
import type { ApiResponse, LC_ErrorOut } from '@/api/shared/types';
import type { NotificationType } from '@/store/slices/uiSlice';


export type ResponseLike =
  | { error?: LC_ErrorOut; success: boolean; status?: number }
  | Pick<ApiResponse<unknown>, 'error' | 'success' | 'status'>
  | null
  | undefined;

/**
 * HTTP 403 responses are already surfaced globally by the forbidden handler
 * (see useAuthErrorHandler). Notifying them again here would duplicate the
 * notification and leak the raw backend `detail` payload.
 */
const FORBIDDEN_STATUS = 403;

export type ApiErrorFallback = string | (() => string);

type ShowNotificationFn = (type: NotificationType, message: string) => void;

function resolveFallback(fallback: ApiErrorFallback | undefined): string {
  if (typeof fallback === 'function') return fallback();
  return fallback ?? i18n.t('common:errors.unexpected');
}

export function extractApiErrorMessage(
  response: ResponseLike,
  fallback?: ApiErrorFallback,
): string | null {
  const error = response?.error;
  if (!error) return null;
  return error.detail?.trim() || resolveFallback(fallback);
}

export function notifyApiError(
  response: ResponseLike,
  showNotification: ShowNotificationFn,
  fallback?: ApiErrorFallback,
): string | null {
  if (!response?.error) return null;
  // Permission errors (403) are notified globally by the forbidden handler.
  // Suppress here to avoid a duplicate notification with the raw backend detail.
  if (response.status === FORBIDDEN_STATUS) return null;
  const message = extractApiErrorMessage(response, fallback) ?? resolveFallback(fallback);
  showNotification('error', message);
  return message;
}

